import React, { useEffect, useState } from 'react';
import {
  FIELD_APK_NAME,
  clearInstallDismissed,
  installForcedByUrl,
  isAndroidPhone,
  isPhoneOrTabletDevice,
  isStandaloneApp,
  readDismissedAt,
  rememberInstallDismissed,
  resolveFieldApkHref,
  shouldShowInstallPrompt,
} from './installPrompt';
import './mobile.css';

export const InstallAppPrompt = () => {
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [apkHref, setApkHref] = useState(null);
  const android = isAndroidPhone();

  useEffect(() => {
    const force = installForcedByUrl();
    if (force) clearInstallDismissed();
    const phone = isPhoneOrTabletDevice();
    const standalone = isStandaloneApp();
    setOpen(shouldShowInstallPrompt({
      standalone,
      phone,
      force,
      dismissedAt: readDismissedAt(),
    }));
  }, []);

  useEffect(() => {
    const onPrompt = (event) => {
      event.preventDefault();
      setDeferred(event);
      if (shouldShowInstallPrompt({
        standalone: isStandaloneApp(),
        phone: isPhoneOrTabletDevice(),
        force: installForcedByUrl(),
        dismissedAt: readDismissedAt(),
      })) {
        setOpen(true);
      }
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  useEffect(() => {
    let cancelled = false;
    resolveFieldApkHref()
      .then((href) => { if (!cancelled) setApkHref(href); })
      .catch(() => { if (!cancelled) setApkHref(null); });
    return () => { cancelled = true; };
  }, []);

  if (!open || isStandaloneApp()) return null;

  const close = () => {
    rememberInstallDismissed();
    setOpen(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice?.outcome === 'accepted') close();
      return;
    }
    if (android && apkHref) {
      window.location.href = apkHref;
      return;
    }
    // iOS / no APK — send them to the download instructions page.
    window.location.href = '/download-app';
  };

  const primaryLabel = deferred
    ? 'Install'
    : android && apkHref
      ? 'Download Android APK'
      : 'How to install';

  return (
    <div className="lgl-m-install" role="dialog" aria-modal="true" aria-label="Install LGL Portal">
      <div className="lgl-m-installcard">
        <span className="lgl-m-installlogochip">
          <img src="/lgl-mark.png" alt="LGL" className="lgl-m-installlogo" />
        </span>
        <p className="lgl-m-kicker">LGL</p>
        <h2>Install LGL Portal</h2>
        <p>
          This is the phone app — not the desktop website. Download it once,
          then open it from your home screen.
        </p>
        <button type="button" className="lgl-m-installprimary" onClick={install}>
          {primaryLabel}
        </button>
        {android && apkHref ? (
          <a className="lgl-m-installapk" href={apkHref} download={FIELD_APK_NAME}>
            Or tap here to download the APK
          </a>
        ) : (
          <a className="lgl-m-installapk" href="/download-app">
            Open download instructions
          </a>
        )}
        <p className="lgl-m-installhint">
          {android
            ? 'This is a new LGL Portal app, not an update of Field Progress. Install it, then delete the old Field Progress icon if it is still there. Samsung will ask to allow installs from Chrome, then Camera.'
            : 'On iPhone/iPad: use Safari → Share → Add to Home Screen.'}
        </p>
        <button
          type="button"
          className="lgl-m-installbrowser"
          onClick={() => {
            close();
            window.location.href = '/download-app';
          }}
        >
          More download options
        </button>
        <button type="button" className="lgl-m-installlater" onClick={close}>Not now</button>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
